/**
 * Helpers for the want-ad owner's reply on the introduction accept screen
 * (ReceiveIntroMessage). Messages go straight from the browser to Firestore,
 * so the other agent only hears about a reply if the screen also (a) marks the
 * intro unread for them - which is what the hourly unread-introduction reminder
 * keys on - and (b) calls the API's chatMail endpoint for the immediate email.
 * This screen did neither, so these replies were never notified.
 */

const FALLBACK_NAME = "the want-listing owner";
const FALLBACK_TITLE = "your introduction";

/**
 * The reply comes from the want-ad owner, so the unread flag to raise is the
 * inventory owner's - the same flag MessagesRightSection sets in that case.
 */
export function buildIntroReplyUpdate({ message, senderId }) {
  return {
    isrepiled: true,
    latestMessage: message,
    lastestMessageSentUserId: senderId,
    isInventoryOwnerHasUnreadMessage: true,
  };
}

/**
 * Requests the "new message" email for a reply that just saved. Never throws:
 * the reply is already delivered in-app, so a failed email request must not
 * turn a successful send into an error for the sender.
 *
 * @param {Function} sendEmail - services/messagesService saveChatEmail
 * @param {boolean} opts.messageSaved - the Firestore message write succeeded
 * @returns {Promise<boolean>} true when the email request was accepted
 */
export async function notifyIntroReplyRecipient(
  sendEmail,
  { recipientId, introId, messageSaved, senderName, title },
) {
  // Never announce a message that is not in the thread.
  if (!messageSaved) return false;
  if (!recipientId || !introId) {
    console.warn("Introduction reply email skipped: no recipient", {
      introId,
      recipientId,
    });
    return false;
  }
  try {
    const response = await sendEmail({
      userId: recipientId,
      introId,
      name: (senderName || "").trim() || FALLBACK_NAME,
      title: (title || "").trim() || FALLBACK_TITLE,
    });
    return response?.errorCode === 0;
  } catch (error) {
    console.error("Introduction reply email request failed", error);
    return false;
  }
}
